import { c as _c } from "react/compiler-runtime";
import { Form, LoadingSpinner, PageShell, SuccessCheck } from '@onefootprint/ui';
import type { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { renderScrubbedText } from './render-scrubbed-text';
import { ResendButton } from './resend-button';
type ErrorCallback = (message: string) => void;
type ChallengeKind = 'sms' | 'email';
type TFn = ReturnType<typeof useTranslation<'idv2'>>['t'];
const getTitle = (t: TFn, kind: ChallengeKind, isReturningUser: boolean): string => {
  if (isReturningUser) return t($ => $.identify.otp.welcomeBack);
  return t($ => $.identify.otp[kind].title);
};
const getSubtitle = (t: TFn, kind: ChallengeKind, contactInfo?: string): string => {
  if (contactInfo && kind === 'email') return t($ => $.identify.otp.email.subtitleWithEmail, {
    email: contactInfo
  });
  if (contactInfo && kind === 'sms') return t($ => $.identify.otp.sms.subtitleWithPhone, {
    phone: contactInfo
  });
  return t($ => $.identify.otp[kind].subtitle);
};
const getSubtitleNode = (subtitle: string, challengeKind: ChallengeKind | undefined, contactInfo?: string): ReactNode => {
  if (!contactInfo || !challengeKind) return renderScrubbedText(subtitle);
  const actionName = challengeKind === 'email' ? 'Subtitle with email' : 'Subtitle with phone';
  return <span data-dd-privacy="mask" data-dd-action-name={actionName}>
      {renderScrubbedText(subtitle)}
    </span>;
};
export type OtpChallengeProps = {
  challengeKind?: ChallengeKind;
  contactInfo?: string;
  hint?: string;
  isReturningUser?: boolean;
  onSubmit?: (challengeResponse: string, onError: ErrorCallback) => void;
  onClose?: () => void;
  onResend?: () => void;
  isLoading?: boolean;
  isResendLoading?: boolean;
  isVerifying?: boolean;
  isSuccess?: boolean;
  resendDisabledUntil?: Date;
  showLegalFooter?: boolean;
  headerIcon?: ReactNode;
};
type OtpChallengeFormData = {
  otp: string;
};
export const OtpChallenge = t0 => {
  const $ = _c(65);
  const {
    challengeKind,
    contactInfo,
    hint,
    isReturningUser: t1,
    onSubmit,
    isLoading: t2,
    isResendLoading: t3,
    isVerifying: t4,
    isSuccess: t5,
    onResend,
    resendDisabledUntil,
    showLegalFooter: t6,
    headerIcon
  } = t0;
  const isReturningUser = t1 === undefined ? false : t1;
  const isLoading = t2 === undefined ? false : t2;
  const isResendLoading = t3 === undefined ? false : t3;
  const isVerifying = t4 === undefined ? false : t4;
  const isSuccess = t5 === undefined ? false : t5;
  const showLegalFooter = t6 === undefined ? true : t6;
  const {
    t
  } = useTranslation("idv2");
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      defaultValues: {
        otp: ""
      }
    };
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const methods = useForm(t7);
  let t8;
  if ($[1] !== methods || $[2] !== onSubmit) {
    t8 = value => {
      onSubmit?.(value, message => {
        methods.setError("otp", {
          message
        });
        methods.setValue("otp", "");
      });
    };
    $[1] = methods;
    $[2] = onSubmit;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const handleComplete = t8;
  const kind = challengeKind ?? "sms";
  let t9;
  if ($[4] !== isReturningUser || $[5] !== kind || $[6] !== t) {
    t9 = getTitle(t, kind, isReturningUser);
    $[4] = isReturningUser;
    $[5] = kind;
    $[6] = t;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const title = t9;
  let t10;
  if ($[8] !== challengeKind || $[9] !== contactInfo || $[10] !== kind || $[11] !== t) {
    const subtitle = getSubtitle(t, kind, contactInfo);
    t10 = getSubtitleNode(subtitle, challengeKind, contactInfo);
    $[8] = challengeKind;
    $[9] = contactInfo;
    $[10] = kind;
    $[11] = t;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const subtitleNode = t10;
  if (isSuccess) {
    let t11;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <SuccessCheck animationStart={true} size={32} />;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    let t12;
    if ($[14] !== t) {
      t12 = t(_temp);
      $[14] = t;
      $[15] = t12;
    } else {
      t12 = $[15];
    }
    let t13;
    if ($[16] !== t12) {
      t13 = <div className="flex flex-col justify-center items-center h-[100px] gap-3">{t11}<p className="text-label-2 text-success fp-text">{t12}</p></div>;
      $[16] = t12;
      $[17] = t13;
    } else {
      t13 = $[17];
    }
    let t14;
    if ($[18] !== headerIcon || $[19] !== subtitleNode || $[20] !== t13 || $[21] !== title) {
      t14 = <PageShell headerIcon={headerIcon} title={title} subtitle={subtitleNode}>{t13}</PageShell>;
      $[18] = headerIcon;
      $[19] = subtitleNode;
      $[20] = t13;
      $[21] = title;
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    return t14;
  }
  if (isVerifying) {
    let t11;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="flex justify-center items-center h-8"><LoadingSpinner size="xlarge" /></div>;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] !== t) {
      t12 = t(_temp2);
      $[24] = t;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    let t13;
    if ($[26] !== t12) {
      t13 = <div className="flex flex-col justify-center items-center h-[100px] gap-3">{t11}<p className="text-label-2 text-primary fp-text">{t12}</p></div>;
      $[26] = t12;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    let t14;
    if ($[28] !== headerIcon || $[29] !== subtitleNode || $[30] !== t13 || $[31] !== title) {
      t14 = <PageShell headerIcon={headerIcon} title={title} subtitle={subtitleNode}>{t13}</PageShell>;
      $[28] = headerIcon;
      $[29] = subtitleNode;
      $[30] = t13;
      $[31] = title;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    return t14;
  }
  let t11;
  if ($[33] !== t) {
    t11 = t(_temp3);
    $[33] = t;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t11) {
    t12 = {
      required: t11
    };
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== handleComplete || $[38] !== isLoading || $[39] !== t12) {
    t13 = <Form.Otp name="otp" rules={t12} disabled={isLoading} onComplete={handleComplete} autoFocus={true} />;
    $[37] = handleComplete;
    $[38] = isLoading;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== hint) {
    t14 = hint && <p className="text-body-2 text-secondary text-center w-full">{hint}</p>;
    $[41] = hint;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== isLoading) {
    t15 = isLoading && <LoadingSpinner className="min-h-[20px]" />;
    $[43] = isLoading;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t13 || $[46] !== t14 || $[47] !== t15) {
    t16 = <div className="w-full flex flex-col items-center gap-2">{t13}{t14}{t15}</div>;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== isResendLoading || $[50] !== onResend || $[51] !== resendDisabledUntil || $[52] !== showLegalFooter) {
    t17 = onResend && <ResendButton onResend={onResend} isResendLoading={isResendLoading} resendDisabledUntil={resendDisabledUntil} showLegalFooter={showLegalFooter} />;
    $[49] = isResendLoading;
    $[50] = onResend;
    $[51] = resendDisabledUntil;
    $[52] = showLegalFooter;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== t16 || $[55] !== t17) {
    t18 = <div className="flex flex-col gap-6 items-center w-full">{t16}{t17}</div>;
    $[54] = t16;
    $[55] = t17;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== methods || $[58] !== t18) {
    t19 = <FormProvider {...methods}>{t18}</FormProvider>;
    $[57] = methods;
    $[58] = t18;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== headerIcon || $[61] !== subtitleNode || $[62] !== t19 || $[63] !== title) {
    t20 = <PageShell headerIcon={headerIcon} title={title} subtitle={subtitleNode}>{t19}</PageShell>;
    $[60] = headerIcon;
    $[61] = subtitleNode;
    $[62] = t19;
    $[63] = title;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  return t20;
};
export default OtpChallenge;
function _temp($) {
  return $.identify.otp.success;
}
function _temp2($_0) {
  return $_0.identify.otp.verifying;
}
function _temp3($_1) {
  return $_1.identify.otp.errors.invalidCode;
}